import React, { useEffect, useMemo, useRef } from 'react';
import DancerThumbnail from '../DancerThumbnail';
import { PREDICTION_AGES, PREDICTION_DISCIPLINES, LATIN_DANCES } from '../utils/predictionCategories';

/* ── Icons (inline SVG, inherit currentColor) ─────────────────────────────── */
const Svg = ({ children, size = 16, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

export const IconCheck = (p) => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>;
export const IconX = (p) => <Svg {...p}><path d="M18 6 6 18M6 6l12 12" /></Svg>;
export const IconLock = (p) => <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>;
export const IconShield = (p) => <Svg {...p}><path d="M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" /></Svg>;
export const IconInfo = (p) => <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></Svg>;
export const IconTrophy = (p) => <Svg {...p}><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" /><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" /></Svg>;
export const IconUsers = (p) => <Svg {...p}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6" /></Svg>;
export const IconChart = (p) => <Svg {...p}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></Svg>;

const initials = (name) =>
  String(name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

/**
 * Photo when we have one, initials otherwise – the box never changes size.
 * `photoName` is the database dancer to load the picture for (null = don't look one up).
 */
export function Avatar({ name, photoName, size = 44 }) {
  const lookup = photoName === undefined ? name : photoName;
  return (
    <span className="pw-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}>
      <span className="pw-avatar-initials">{initials(name)}</span>
      {lookup && (
        <DancerThumbnail
          dancerName={lookup}
          size={size}
          showPlaceholder={false}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
        />
      )}
    </span>
  );
}

/** Which database dancer a start-list entry is linked to (only linked/approved names get a photo). */
export const photoNameOf = (entry) => {
  if (entry.match === 'pending' || entry.match === 'none') return null;
  return entry.dancerKey || entry.name;
};

const ageIndex = (a) => PREDICTION_AGES.indexOf(a);
const danceIndex = (d) => LATIN_DANCES.indexOf(d);

/** Short chip text for a category inside its strip. */
const stripLabel = (c) => (c.discipline === 'Latin' ? c.dance || c.label : c.ageCategory || c.label);

const CONFIDENCE_TEXT = {
  none: 'No results in the last 365 days',
  low: 'Low confidence',
  medium: 'Medium confidence',
  high: 'High confidence'
};

/** Admin-only model estimate shown on a contender card. */
function ProbPill({ stat, state }) {
  if (state === 'loading' || (state === 'idle' && !stat)) return <span className="pw-prob loading" aria-hidden="true" />;
  if (!stat) return null;
  const weak = stat.confidence === 'none' || stat.confidence === 'low';
  const record = `${stat.h2h.wins}–${stat.h2h.losses}${stat.h2h.ties ? `–${stat.h2h.ties}` : ''}`;
  const tip = [
    `Model win probability (admin only): ${stat.probability}%`,
    `${stat.recentResults} result${stat.recentResults === 1 ? '' : 's'} in the last 365 days`,
    stat.h2h.met ? `Head-to-head vs this field: ${record} (${stat.h2h.met} opponent${stat.h2h.met === 1 ? '' : 's'})` : 'Has not met anyone in this field',
    CONFIDENCE_TEXT[stat.confidence]
  ].join('\n');
  return (
    <span className={`pw-prob ${weak ? 'weak' : ''}`} title={tip}>
      <IconChart size={11} />
      {weak ? '~' : ''}{Math.round(stat.probability)}%
    </span>
  );
}

const groupStrip = (cats, activeId, scope) => cats.map((c) => ({ ...c, scope, active: c.id === activeId }));

/** Horizontal, swipeable strip of chips. Keeps the active chip in view. */
function Strip({ label, children, activeKey }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current && ref.current.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [activeKey]);
  return (
    <div className="pw-strip-block">
      <div className="pw-strip-label">{label}</div>
      <div className="pw-strip" ref={ref} role="group" aria-label={label}>{children}</div>
    </div>
  );
}

/**
 * Presentational ballot: category navigation, contender cards and the action footer.
 * All data and callbacks come from props (see VotePanel in PredictWinner.js).
 *
 * phase: 'open' | 'closed' | 'settled'
 */
export default function PredictionBallot({
  categories,
  activeId,
  onSelectCategory,
  picks,
  savedPicks,
  onPick,
  phase,
  interactive,
  signedIn,
  onSignIn,
  getPercent,
  showPercents,
  voters,
  verifiedName,
  score,
  saveState,
  hasVoted,
  onSubmit,
  previewNote,
  adminInsights // { state: 'idle'|'loading'|'ready'|'error', data, onRefresh } – admin only
}) {
  const active = categories.find((c) => c.id === activeId) || categories[0];
  const settled = phase === 'settled';
  const insightStats = adminInsights && adminInsights.data && adminInsights.data.categories[active.id];
  const statFor = (name) => insightStats && insightStats.contenders.find((c) => c.name === name);

  const pickedCount = categories.filter((c) => picks[c.id]).length;
  const progress = categories.length ? Math.round((pickedCount / categories.length) * 100) : 0;

  /** settled: true = my pick won, false = missed, null = no pick / not scored yet */
  const outcome = (c) => {
    const mine = savedPicks && savedPicks[c.id];
    if (!settled || !c.winner || !mine) return null;
    return mine === c.winner;
  };
  const stateOf = (c) => {
    if (settled) {
      const o = outcome(c);
      return o === true ? 'correct' : o === false ? 'wrong' : 'none';
    }
    return picks[c.id] ? 'picked' : 'empty';
  };

  /* ── navigation model: discipline → (Latin: age → dance) | (others: age) ── */
  const nav = useMemo(() => {
    const byDiscipline = new Map();
    categories.forEach((c) => {
      if (!byDiscipline.has(c.discipline)) byDiscipline.set(c.discipline, []);
      byDiscipline.get(c.discipline).push(c);
    });
    const disciplines = PREDICTION_DISCIPLINES.filter((d) => byDiscipline.has(d))
      .concat([...byDiscipline.keys()].filter((d) => !PREDICTION_DISCIPLINES.includes(d)));
    return { byDiscipline, disciplines };
  }, [categories]);

  const discipline = active.discipline;
  const inDiscipline = nav.byDiscipline.get(discipline) || [];
  const isLatin = discipline === 'Latin';

  const agesOf = (cats) => [...new Set(cats.map((c) => c.ageCategory))].sort((a, b) => ageIndex(a) - ageIndex(b));
  const latinAges = isLatin ? agesOf(inDiscipline) : [];
  const dancesForAge = isLatin
    ? inDiscipline.filter((c) => c.ageCategory === active.ageCategory).sort((a, b) => danceIndex(a.dance) - danceIndex(b.dance))
    : [];
  const ageStrip = !isLatin ? [...inDiscipline].sort((a, b) => ageIndex(a.ageCategory) - ageIndex(b.ageCategory)) : [];

  const pickFirst = (cats, preferred) => {
    const list = [...cats].sort((a, b) => ageIndex(a.ageCategory) - ageIndex(b.ageCategory) || danceIndex(a.dance) - danceIndex(b.dance));
    return (preferred && list.find(preferred)) || list[0];
  };

  const selectDiscipline = (d) => {
    const cats = nav.byDiscipline.get(d);
    const target = pickFirst(cats, (c) => c.ageCategory === active.ageCategory && (c.dance === active.dance || d !== 'Latin'));
    if (target) onSelectCategory(target.id);
  };
  const selectLatinAge = (age) => {
    const cats = inDiscipline.filter((c) => c.ageCategory === age);
    const target = pickFirst(cats, (c) => c.dance === active.dance);
    if (target) onSelectCategory(target.id);
  };

  const disciplineProgress = (d) => {
    const cats = nav.byDiscipline.get(d);
    const done = settled ? cats.filter((c) => outcome(c) === true).length : cats.filter((c) => picks[c.id]).length;
    return { done, total: cats.length };
  };

  const total = active.startlist.length;
  // Signed-out visitors can look, but tapping a contender takes them to sign in
  const promptSignIn = phase === 'open' && !signedIn;

  return (
    <div className="pw-ballot">
      <div className="pw-scroll">
        {previewNote && !adminInsights && (
          <div className="pw-banner info"><IconInfo /><span>{previewNote}</span></div>
        )}

        {adminInsights && (
          <div className="pw-banner admin">
            <IconChart />
            <span className="pw-banner-grow">
              <strong>Admin only — win probability.</strong>{' '}
              Model estimate from the last 365 days of head-to-head results. Never shown to the public.
              {adminInsights.state === 'loading' && ' Calculating…'}
              {adminInsights.state === 'error' && ` ${adminInsights.message || 'Could not calculate.'}`}
              {adminInsights.state === 'ready' && adminInsights.data && ` Based on ${adminInsights.data.resultLists} result lists.`}
            </span>
            <button type="button" className="pw-text-btn" onClick={adminInsights.onRefresh} disabled={adminInsights.state === 'loading'}>
              {adminInsights.state === 'error' ? 'Retry' : 'Recalculate'}
            </button>
          </div>
        )}

        {phase === 'open' && signedIn && !adminInsights && (
          verifiedName ? (
            <div className="pw-banner success">
              <IconShield />
              <span>
                <strong>Verified dancer · {verifiedName}.</strong> Each correct prediction adds a point to your public profile.
              </span>
            </div>
          ) : (
            <div className="pw-banner info">
              <IconInfo />
              <span>
                Open to everyone. Prediction points are added to the profiles of <strong>verified dancers</strong> only.
              </span>
            </div>
          )
        )}
        {phase === 'closed' && (
          <div className="pw-banner neutral"><IconLock /><span>Voting is closed. Results are published after the competition.</span></div>
        )}

        {settled && signedIn && score && score.scored > 0 && (
          <div className={`pw-result ${score.correct === score.scored ? 'perfect' : ''}`}>
            <div className="pw-result-figure">
              <span className="pw-result-num">{score.correct}</span>
              <span className="pw-result-of">/ {score.scored}</span>
            </div>
            <div className="pw-result-text">
              <strong>{score.correct === score.scored ? 'Perfect score' : 'Your result'}</strong>
              <span>
                {score.correct} correct {score.correct === 1 ? 'prediction' : 'predictions'} out of {score.scored} scored categories.
              </span>
              {score.pointsNode}
            </div>
          </div>
        )}
        {settled && signedIn && !hasVoted && (
          <div className="pw-banner neutral"><IconInfo /><span>You did not submit a prediction for this competition.</span></div>
        )}

        {/* ── Category navigation ─────────────────────────────────────── */}
        <nav className="pw-nav" aria-label="Categories">
          <div className="pw-disciplines" role="tablist" aria-label="Discipline">
            {nav.disciplines.map((d) => {
              const p = disciplineProgress(d);
              return (
                <button
                  key={d}
                  type="button"
                  role="tab"
                  aria-selected={d === discipline}
                  className={`pw-discipline ${d === discipline ? 'active' : ''}`}
                  onClick={() => selectDiscipline(d)}
                >
                  {d}
                  <span className={`pw-count-badge ${p.done === p.total ? 'full' : ''}`}>{p.done}/{p.total}</span>
                </button>
              );
            })}
          </div>

          {isLatin ? (
            <>
              <Strip label="Age category" activeKey={`${discipline}-${active.ageCategory}`}>
                {latinAges.map((age) => {
                  const cats = inDiscipline.filter((c) => c.ageCategory === age);
                  const done = settled ? cats.filter((c) => outcome(c) === true).length : cats.filter((c) => picks[c.id]).length;
                  const isActive = age === active.ageCategory;
                  return (
                    <button
                      key={age}
                      type="button"
                      className={`pw-chip ${isActive ? 'active' : ''}`}
                      data-active={isActive}
                      aria-pressed={isActive}
                      onClick={() => selectLatinAge(age)}
                    >
                      {age}
                      <span className={`pw-chip-count ${done === cats.length ? 'full' : ''}`}>{done}/{cats.length}</span>
                    </button>
                  );
                })}
              </Strip>
              <Strip label={`Dance · ${active.ageCategory}`} activeKey={active.id}>
                {dancesForAge.map((c) => {
                  const st = stateOf(c);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`pw-chip ${c.id === active.id ? 'active' : ''}`}
                      data-active={c.id === active.id}
                      aria-pressed={c.id === active.id}
                      onClick={() => onSelectCategory(c.id)}
                    >
                      <span className={`pw-state ${st}`} aria-hidden="true">
                        {(st === 'picked' || st === 'correct') && <IconCheck size={11} />}
                        {st === 'wrong' && <IconX size={11} />}
                      </span>
                      {stripLabel(c)}
                      <span className="pw-sr">
                        {st === 'picked' && ' — prediction selected'}
                        {st === 'correct' && ' — correct'}
                        {st === 'wrong' && ' — incorrect'}
                      </span>
                    </button>
                  );
                })}
              </Strip>
            </>
          ) : (
            <Strip label="Age category" activeKey={active.id}>
              {groupStrip(ageStrip, active.id, discipline).map((c) => {
                const st = stateOf(c);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`pw-chip ${c.active ? 'active' : ''}`}
                    data-active={c.active}
                    aria-pressed={c.active}
                    onClick={() => onSelectCategory(c.id)}
                  >
                    <span className={`pw-state ${st}`} aria-hidden="true">
                      {(st === 'picked' || st === 'correct') && <IconCheck size={11} />}
                      {st === 'wrong' && <IconX size={11} />}
                    </span>
                    {stripLabel(c)}
                    <span className="pw-sr">
                      {st === 'picked' && ' — prediction selected'}
                      {st === 'correct' && ' — correct'}
                      {st === 'wrong' && ' — incorrect'}
                    </span>
                  </button>
                );
              })}
            </Strip>
          )}
        </nav>

        {/* ── Contenders ──────────────────────────────────────────────── */}
        <section className="pw-main" aria-labelledby="pw-active-title">
          <header className="pw-main-head">
            <div>
              <h4 id="pw-active-title">{active.label}</h4>
              <p>
                {total} contender{total === 1 ? '' : 's'}
                {settled ? ' · final result' : phase === 'open' ? ' · select one winner' : ''}
              </p>
            </div>
            {interactive && picks[active.id] && (
              <button type="button" className="pw-text-btn" onClick={() => onPick(active.id, '')}>Clear</button>
            )}
          </header>

          <div className="pw-options" role="radiogroup" aria-label={`${active.label} – choose the winner`}>
            {active.startlist.map((d) => {
              const selected = !settled && picks[active.id] === d.name;
              const isWinner = settled && active.winner === d.name;
              const mine = savedPicks && savedPicks[active.id] === d.name;
              const pct = showPercents ? getPercent(active.id, d.name) : null;
              const stat = adminInsights ? statFor(d.name) : null;
              return (
                <label
                  key={d.name}
                  className={[
                    'pw-option',
                    selected ? 'selected' : '',
                    isWinner ? 'winner' : '',
                    settled && mine && !isWinner ? 'missed' : '',
                    interactive || promptSignIn ? 'interactive' : ''
                  ].join(' ')}
                  onClick={promptSignIn ? (e) => { e.preventDefault(); onSignIn(); } : undefined}
                >
                  <input
                    type="radio"
                    className="pw-radio-input"
                    name={`pw-${active.id}`}
                    checked={selected}
                    disabled={!interactive}
                    onChange={() => onPick(active.id, d.name)}
                  />
                  <Avatar name={d.name} photoName={photoNameOf(d)} size={36} />
                  <span className="pw-option-body">
                    <span className="pw-option-name">{d.name}</span>
                    <span className="pw-option-sub">
                      <span className="pw-option-studio">{d.studio || ' '}</span>
                      {adminInsights && <ProbPill stat={stat} state={adminInsights.state} />}
                    </span>
                  </span>
                  <span className="pw-option-end">
                    {isWinner && (
                      <span className="pw-badge-icon gold" title="Winner"><IconTrophy size={12} /><span className="pw-sr">Winner</span></span>
                    )}
                    {settled && mine && (
                      <span className={`pw-badge-icon ${isWinner ? 'brand' : 'red'}`} title="Your pick">{isWinner ? <IconCheck size={12} /> : <IconX size={12} />}<span className="pw-sr">Your pick</span></span>
                    )}
                    {pct != null ? (
                      <span className="pw-option-pct" title="Share of all predictions in this category">
                        {pct}<small>%</small>
                      </span>
                    ) : (
                      !settled && <span className="pw-radio" aria-hidden="true"><IconCheck size={12} /></span>
                    )}
                  </span>
                  {pct != null && <span className="pw-option-bar" style={{ width: `${pct}%` }} />}
                </label>
              );
            })}
          </div>

          {adminInsights && adminInsights.state === 'ready' && insightStats && (
            <div className="pw-insight">
              <div className="pw-insight-head">
                <IconChart size={14} />
                <strong>Win probability · admin only</strong>
                <span>
                  Bradley–Terry rating from who placed ahead of whom in {insightStats.competitions} result lists of this category
                  (last 365 days). Recent results count more; a dancer with no results is rated as average.
                </span>
              </div>
              <div className="pw-insight-table" role="table">
                <div className="pw-insight-row head" role="row">
                  <span role="columnheader">Dancer</span>
                  <span role="columnheader">Win %</span>
                  <span role="columnheader">Results</span>
                  <span role="columnheader">Head-to-head</span>
                  <span role="columnheader">Avg place</span>
                  <span role="columnheader">Confidence</span>
                </div>
                {[...insightStats.contenders].sort((a, b) => b.probability - a.probability).map((c) => (
                  <div className="pw-insight-row" role="row" key={c.name}>
                    <span role="cell" className="pw-insight-name">{c.name}</span>
                    <span role="cell"><strong>{c.probability}%</strong></span>
                    <span role="cell">{c.recentResults}</span>
                    <span role="cell">{c.h2h.met ? `${c.h2h.wins}–${c.h2h.losses}${c.h2h.ties ? `–${c.h2h.ties}` : ''} vs ${c.h2h.met}` : '—'}</span>
                    <span role="cell">{c.avgPlace != null ? c.avgPlace : '—'}</span>
                    <span role="cell"><span className={`pw-conf ${c.confidence}`}>{c.confidence}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <footer className="pw-footer">
        {phase === 'open' && signedIn && interactive ? (
          <>
            <div className="pw-progress">
              <div className="pw-progress-label">
                <strong>{pickedCount}</strong> of {categories.length} categories predicted
              </div>
              <div className="pw-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                <span style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="pw-footer-actions" aria-live="polite">
              {saveState === 'saved' && <span className="pw-status-text ok"><IconCheck size={14} /> Saved</span>}
              {saveState === 'dirty' && <span className="pw-status-text">Unsaved changes</span>}
              {saveState === 'error' && <span className="pw-status-text err">Could not save. Voting may have closed.</span>}
              <button
                type="button"
                className="pw-btn primary"
                disabled={saveState === 'saving' || saveState === 'saved' || pickedCount === 0}
                onClick={onSubmit}
              >
                {saveState === 'saving' ? 'Saving…' : hasVoted ? 'Update predictions' : 'Submit predictions'}
              </button>
            </div>
          </>
        ) : phase === 'open' && !signedIn ? (
          <>
            <div className="pw-progress"><div className="pw-progress-label">Sign in to submit your predictions.</div></div>
            <div className="pw-footer-actions">
              <button type="button" className="pw-btn primary" onClick={onSignIn}>Sign in</button>
            </div>
          </>
        ) : (
          <div className="pw-progress">
            <div className="pw-progress-label meta">
              <IconUsers size={14} /> {voters > 0 ? `${voters} ${voters === 1 ? 'participant' : 'participants'}` : 'No predictions yet'}
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}
